/* eslint-disable no-console */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type * as Ably from 'ably';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { useRFQStore } from 'pages/auto-rfq/store';
import { AutoRFQQueryKey } from 'Services';
import {
  ablySpaceChannelFor,
  ensureResource,
  releaseResource
} from 'lib/realtime/AblyClient';

type EventSource = 'user' | 'ingest' | 'quote-sync' | 'pricing-sync' | 'system';

interface BasePayload {
  source: EventSource;
  actor?: { userId: string; name: string };
}
interface NoteAddedPayload extends BasePayload {
  itemId: string;
  rfqId: string;
  note: { _id: string };
}
interface NoteUpdatedPayload extends BasePayload {
  itemId: string;
  rfqId: string;
  note: { _id: string };
}
interface NoteDeletedPayload extends BasePayload {
  itemId: string;
  rfqId: string;
  noteId: string;
}
interface CounterChangedPayload extends BasePayload {
  itemId: string;
  field: string;
  delta: number;
}

const EVENT_NOTE_ADDED = 'rfq_item.note_added';
const EVENT_NOTE_UPDATED = 'rfq_item.note_updated';
const EVENT_NOTE_DELETED = 'rfq_item.note_deleted';
const EVENT_COUNTER_CHANGED = 'rfq_item.counter_changed';

/**
 * Subscribes to the four note-related events the backend emits on
 * `rfq:{rfqId}` and reconciles them into local state:
 *
 *   - `rfq_item.note_added` / `note_updated` / `note_deleted` invalidate
 *     `[GET_INTERNAL_NOTES, itemId]`. If a notes dialog is currently open
 *     on that item, React Query refetches and the new state appears. If
 *     no one is observing the query, the cache simply becomes stale and
 *     refetches the next time a dialog opens — no wasted network.
 *
 *   - `rfq_item.counter_changed` (field=`internalNotesCount`) bumps the
 *     row's badge count directly in the Zustand store, so the "5 notes"
 *     indicator updates even when no dialog is open.
 *
 * Self-echo filter: `source === 'user'` plus matching `actor.userId`
 * means the current user is the originator — their local mutation already
 * applied the change optimistically + invalidated the cache; ignore the
 * broadcast or we'll double-bump the counter.
 */
export const useRfqInternalNotesRealtime = (rfqId: string | undefined) => {
  const { user } = useAppContext();
  const queryClient = useQueryClient();
  const updateProcessedProduct = useRFQStore(s => s.updateProcessedProduct);

  useEffect(() => {
    if (!rfqId) return undefined;
    let cancelled = false;
    let channel: Ably.RealtimeChannel | null = null;
    const handlers: Record<string, (m: Ably.Message) => void> = {};
    const currentUserId = user?.ID ? String(user.ID) : null;

    const isSelfEcho = (payload: BasePayload | undefined) =>
      !!payload &&
      payload.source === 'user' &&
      payload.actor?.userId === currentUserId;

    const invalidateNotes = (itemId: string) =>
      queryClient.invalidateQueries({
        queryKey: [AutoRFQQueryKey.GET_INTERNAL_NOTES, itemId]
      });

    handlers[EVENT_NOTE_ADDED] = (msg: Ably.Message) => {
      const data = msg?.data as NoteAddedPayload | undefined;
      if (!data?.itemId || isSelfEcho(data)) return;
      console.info('[notes-rt] note added', { itemId: data.itemId });
      invalidateNotes(data.itemId);
    };
    handlers[EVENT_NOTE_UPDATED] = (msg: Ably.Message) => {
      const data = msg?.data as NoteUpdatedPayload | undefined;
      if (!data?.itemId || isSelfEcho(data)) return;
      console.info('[notes-rt] note updated', { itemId: data.itemId });
      invalidateNotes(data.itemId);
    };
    handlers[EVENT_NOTE_DELETED] = (msg: Ably.Message) => {
      const data = msg?.data as NoteDeletedPayload | undefined;
      if (!data?.itemId || isSelfEcho(data)) return;
      console.info('[notes-rt] note deleted', {
        itemId: data.itemId,
        noteId: data.noteId
      });
      invalidateNotes(data.itemId);
    };
    handlers[EVENT_COUNTER_CHANGED] = (msg: Ably.Message) => {
      const data = msg?.data as CounterChangedPayload | undefined;
      if (
        !data?.itemId ||
        data.field !== 'internalNotesCount' ||
        isSelfEcho(data)
      ) {
        return;
      }
      const product = useRFQStore
        .getState()
        .processedProducts.find(p => p.id === data.itemId);
      if (!product) return;
      const next = Math.max(0, (product.internalNotesCount || 0) + data.delta);
      console.info('[notes-rt] counter changed', {
        itemId: data.itemId,
        delta: data.delta,
        next
      });
      updateProcessedProduct(data.itemId, { internalNotesCount: next });
    };

    (async () => {
      try {
        const ably = await ensureResource({ type: 'rfq', id: rfqId });
        if (cancelled) return;
        const ch = ably.channels.get(
          ablySpaceChannelFor({ type: 'rfq', id: rfqId })
        );
        channel = ch;
        await Promise.all(
          Object.entries(handlers).map(([event, handler]) =>
            ch.subscribe(event, handler)
          )
        );
      } catch (err) {
        console.warn('[notes-rt] subscribe failed', err);
      }
    })();

    return () => {
      cancelled = true;
      const ch = channel;
      if (ch) {
        Object.entries(handlers).forEach(([event, handler]) => {
          try {
            ch.unsubscribe(event, handler);
          } catch (err) {
            console.warn('[notes-rt] unsubscribe failed', err);
          }
        });
      }
      releaseResource({ type: 'rfq', id: rfqId }).catch(err =>
        console.warn('[notes-rt] release failed', err)
      );
    };
  }, [rfqId, user?.ID, queryClient, updateProcessedProduct]);
};
